import React, { useEffect, useState } from "react";

const AddQualifications = () => {
    const [data, setData] = useState(null);
    const [newQualification, setNewQualification] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [editingQual, setEditingQual] = useState(null);
    const [editValue, setEditValue] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");

    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

    // Fetch qualifications data
    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                setError("");
                const res = await fetch(`${apiBaseUrl}/dataqualifications`);
                if (!res.ok) throw new Error("Failed to load qualifications");
                const result = await res.json();
                setData(result);
            } catch (err) {
                console.error("Fetch error:", err);
                setError("Failed to load qualifications. Please refresh the page.");
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [apiBaseUrl]);

    // Refresh data manually
    const refreshData = async () => {
        try {
            const res = await fetch(`${apiBaseUrl}/dataqualifications`);
            if (!res.ok) throw new Error("Refresh failed");
            const result = await res.json();
            setData(result);
        } catch (err) {
            alert("Could not refresh. Please reload the page.");
        }
    };

    // Helper to safely get qualifications array
    const getQualifications = () => {
        try {
            return Array.isArray(data?.states?.[0]?.districts) ? data.states[0].districts : [];
        } catch {
            return [];
        }
    };

    // Add new qualification
    const handleAddQualification = async () => {
        const trimmed = newQualification.trim();
        if (!trimmed) return alert("Please enter a qualification");

        const quals = getQualifications();
        const lowerTrimmed = trimmed.toLowerCase();

        // Prevent duplicate on add
        if (quals.some((q) => q.toLowerCase() === lowerTrimmed)) {
            return alert(`"${trimmed}" already exists!`);
        }

        try {
            const res = await fetch(`${apiBaseUrl}/addqualifications`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ district: trimmed }),
            });

            const result = await res.json();
            if (!res.ok) throw new Error(result.error || "Failed to add qualification");

            setData(result.data);
            setNewQualification("");
            alert("Qualification added successfully!");
        } catch (err) {
            alert(err.message || "Failed to add qualification");
        }
    };

    // Delete qualification
    const handleDeleteQualification = async (qualification) => {
        if (!window.confirm(`Delete "${qualification}" permanently?`)) return;

        try {
            const res = await fetch(`${apiBaseUrl}/deletequalifications`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ district: qualification }),
            });

            const result = await res.json();
            if (!res.ok) throw new Error(result.error || "Failed to delete");

            setData(result.data);
            alert("Qualification deleted successfully!");
        } catch (err) {
            alert(err.message || "Failed to delete qualification");
        }
    };

    // Save edited qualification
    const handleSaveEdit = async () => {
        const trimmed = editValue.trim();
        if (!trimmed) return alert("Qualification name cannot be empty");
        if (trimmed === editingQual) return cancelEdit();

        const quals = getQualifications();
        const lowerTrimmed = trimmed.toLowerCase();

        // Prevent renaming to existing qualification
        const exists = quals.some((q) => q.toLowerCase() === lowerTrimmed && q !== editingQual);
        if (exists) return alert("This qualification already exists!");

        setIsSaving(true);
        try {
            const res = await fetch(`${apiBaseUrl}/updatequalifications`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    oldDistrict: editingQual,
                    newDistrict: trimmed,
                }),
            });

            const result = await res.json();
            if (!res.ok) throw new Error(result.error || "Update failed");

            setData(result.data);
            cancelEdit();
            alert("Qualification updated successfully!");
        } catch (err) {
            alert(err.message || "Failed to update qualification");
        } finally {
            setIsSaving(false);
        }
    };

    const cancelEdit = () => {
        setEditingQual(null);
        setEditValue("");
        setIsSaving(false);
    };

    const qualifications = getQualifications();
    const filtered = qualifications.filter((q) => q.toLowerCase().includes(searchTerm.toLowerCase()));

    if (isLoading) return <p className="text-center py-20 text-gray-600">Loading qualifications...</p>;
    if (error) return <p className="text-center py-20 text-red-600">{error}</p>;
    return (
        <div className="min-h-screen bg-gray-100 py-8 px-4">
            <div className="max-w-2xl mx-auto bg-white shadow-lg rounded-xl p-6 md:p-8">
                <h1 className="text-3xl font-bold text-center text-gray-800 mb-8">Manage Qualifications</h1>

                {/* Add New */}
                <div className="flex flex-col sm:flex-row gap-3 mb-8">
                    <input
                        type="text"
                        value={newQualification}
                        onChange={(e) => setNewQualification(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleAddQualification()}
                        placeholder="Add new qualification"
                        className="flex-1 px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                        onClick={handleAddQualification}
                        className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition"
                    >
                        Add Qualification
                    </button>
                </div>

                {/* Search */}
                <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search qualifications..."
                    className="w-full mb-6 px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />

                {/* List */}
                <ul className="space-y-3">
                    {filtered.length === 0 ? (
                        <p className="text-center text-gray-500 py-8">No qualifications found</p>
                    ) : (
                        filtered.map((qual) => (
                            <li
                                key={qual}
                                className="flex items-center justify-between bg-gray-50 p-4 rounded-lg border hover:shadow transition"
                            >
                                {editingQual === qual ? (
                                    <div className="flex items-center gap-2 flex-1">
                                        <input
                                            type="text"
                                            value={editValue}
                                            onChange={(e) => setEditValue(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") handleSaveEdit();
                                                if (e.key === "Escape") cancelEdit();
                                            }}
                                            className="flex-1 px-3 py-2 border rounded focus:outline-none focus:ring"
                                            autoFocus
                                        />
                                        <button
                                            onClick={handleSaveEdit}
                                            disabled={isSaving}
                                            className={`px-4 py-2 text-sm text-white rounded transition ${
                                                isSaving ? "bg-gray-400" : "bg-green-500 hover:bg-green-600"
                                            }`}
                                        >
                                            {isSaving ? "Saving..." : "Save"}
                                        </button>
                                        <button
                                            onClick={cancelEdit}
                                            className="px-4 py-2 text-sm text-white bg-gray-500 rounded hover:bg-gray-600"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <span className="font-medium">{qual}</span>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => {
                                                    setEditingQual(qual);
                                                    setEditValue(qual);
                                                }}
                                                className="px-4 py-2 text-sm text-white bg-blue-500 rounded hover:bg-blue-600"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => handleDeleteQualification(qual)}
                                                className="px-4 py-2 text-sm text-white bg-red-500 rounded hover:bg-red-600"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </>
                                )}
                            </li>
                        ))
                    )}
                </ul>

                {/* Refresh Button */}
                <div className="text-center mt-8">
                    <button onClick={refreshData} className="text-sm text-gray-600 hover:text-gray-800 underline">
                        Refresh List
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddQualifications;
